/**
 * Cliente da API.
 *
 * O access token vive SÓ EM MEMÓRIA — nunca em localStorage. Token em
 * localStorage é o que um XSS leva embora; em memória, ele morre com a aba.
 * A continuidade vem do cookie httpOnly de refresh, que o JavaScript não lê.
 */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    readonly detalhes?: { campo: string; erro: string }[],
  ) {
    super(mensagem)
    this.name = 'ErroApi'
  }
}

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3333'

let acessoAtual: string | null = null
let renovando: Promise<boolean> | null = null
const aoPerderSessao = new Set<() => void>()

export function guardarAcesso(token: string | null) {
  acessoAtual = token
}
export function temAcesso() {
  return acessoAtual !== null
}
export function lerAcesso() {
  return acessoAtual
}

export function aoDeslogar(ouvinte: () => void): () => void {
  aoPerderSessao.add(ouvinte)
  // Devolve void, não o boolean do Set.delete: este retorno vira cleanup de
  // useEffect, que recusa qualquer coisa diferente de void.
  return () => {
    aoPerderSessao.delete(ouvinte)
  }
}

/**
 * Renova o access token. Uma renovação por vez: sem isto, cinco requisições
 * falhando juntas disparariam cinco refresh simultâneos — e a rotação do
 * servidor trata concorrência como corrida, devolvendo 409 para as perdedoras.
 */
export async function renovar(): Promise<boolean> {
  if (renovando) return renovando
  renovando = (async () => {
    try {
      const r = await fetch(`${BASE}/v1/auth/refresh`, { method: 'POST', credentials: 'include' })
      if (!r.ok) return false
      const { access } = (await r.json()) as { access: string }
      acessoAtual = access
      return true
    } catch {
      return false
    } finally {
      // Libera só depois que todo mundo que estava esperando leu o resultado.
      setTimeout(() => (renovando = null), 0)
    }
  })()
  return renovando
}

type Opcoes = Omit<RequestInit, 'body'> & { corpo?: unknown; semAuth?: boolean }

export async function api<T = unknown>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const { corpo, semAuth, ...resto } = opcoes

  const chamar = async (): Promise<Response> =>
    fetch(`${BASE}${caminho}`, {
      ...resto,
      credentials: 'include',
      headers: {
        ...(corpo !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(acessoAtual && !semAuth ? { authorization: `Bearer ${acessoAtual}` } : {}),
        ...resto.headers,
      },
      ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {}),
    })

  let r = await chamar()

  // 401 com token em mãos quase sempre é token vencido (15 min). Renova uma
  // vez e repete — a pessoa não deveria ver a sessão piscar a cada 15 minutos.
  if (r.status === 401 && acessoAtual && !semAuth) {
    if (await renovar()) {
      r = await chamar()
    } else {
      acessoAtual = null
      for (const ouvinte of aoPerderSessao) ouvinte()
    }
  }

  if (r.status === 204) return undefined as T
  const texto = await r.text()
  const dados = texto ? JSON.parse(texto) : null

  if (!r.ok) {
    const e = dados?.erro ?? {}
    throw new ErroApi(r.status, e.codigo ?? 'ERRO', e.mensagem ?? 'Falha na comunicação.', e.detalhes)
  }
  return dados as T
}

export const BASE_API = BASE
